import { useState, useEffect } from 'react';
import { X, Users, ArrowRight, ExternalLink } from 'lucide-react';

import logoLight from '@/assets/logo/logo-light.webp';
import coloradoAvLogo from '@/assets/logo/colorado-av-logo.webp';
import residentialImg from '@/assets/images/card_residential_dmi.png';
import draftingImg from '@/assets/images/card_drafting_dmi.png';
import commercialImg from '@/assets/images/card_commercial_av.png';

// Track if the popup has been shown during this specific page load.
// This resets naturally on a hard refresh, but persists during SPA navigation.
let hasShownThisLoad = false;

const GlobalPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (hasShownThisLoad) return;
    hasShownThisLoad = true;
    
    setIsOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });
  }, []);

  const closePopup = () => {
    setIsVisible(false);
    setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 transition-all duration-300 ${isVisible ? 'bg-black/40' : 'bg-black/0'
        }`}
      onClick={closePopup}
    >
      <div
        className={`relative w-full max-w-[920px] rounded-2xl overflow-hidden text-white flex flex-col transition-all duration-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        style={{
          background: 'linear-gradient(145deg, rgba(15, 20, 28, 0.55) 0%, rgba(20, 26, 35, 0.6) 40%, rgba(15, 20, 28, 0.65) 100%)',
          backdropFilter: 'blur(50px) saturate(1.2)',
          WebkitBackdropFilter: 'blur(50px) saturate(1.2)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 32px 80px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
          maxHeight: 'calc(100dvh - 24px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={closePopup}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 text-gray-400 hover:text-white transition-colors z-10 cursor-pointer"
          aria-label="Close popup"
        >
          <X className="w-6 h-6" strokeWidth={2.5} />
        </button>

        <div className="px-5 py-5 sm:px-8 sm:py-6 lg:px-10 lg:py-7 overflow-y-auto">
          {/* Header Section */}
          <div className="flex flex-col items-center text-center mb-4 sm:mb-5">
            <img
              src={logoLight}
              alt="DMI Digital Media Innovations"
              className="h-8 sm:h-10 mb-3 sm:mb-4 object-contain"
            />
            <h2 className="text-2xl sm:text-3xl lg:text-[2.1rem] font-extrabold mb-1.5 leading-tight">
              Three Businesses.{' '}
              <span className="text-[#f37021]">One Family.</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-lg">
              Choose the business that matches what you're looking for.
            </p>
          </div>

          {/* Cards Container */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">

            {/* Card 1: Architectural Drafting (Designs by DMI - this site) */}
            <div className="flex flex-col bg-[#1a1f27]/80 rounded-xl overflow-hidden border-2 border-[#f37021] shadow-[0_0_24px_rgba(243,112,33,0.12)] group transition-all duration-300 hover:shadow-[0_0_32px_rgba(243,112,33,0.2)]">
              <div className="relative h-28 sm:h-32 lg:h-36 overflow-hidden">
                <img
                  src={draftingImg}
                  alt="Drafting and Design Services"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <img
                  src={logoLight}
                  alt="DMI"
                  className="absolute bottom-2 left-3 h-5 sm:h-6 object-contain drop-shadow-lg"
                />
              </div>
              <div className="p-3 sm:p-4 flex flex-col flex-grow">
                <h3 className="text-base sm:text-lg font-bold mb-1 text-white leading-snug">
                  Drafting and Design Services
                </h3>
                <p className="text-gray-400 text-xs sm:text-sm mb-3 flex-grow leading-relaxed">
                  Detailed drafting and documentation for technology projects.
                </p>
                <button
                  onClick={closePopup}
                  className="w-full py-2 px-3 bg-[#f37021] hover:bg-[#e0621a] text-white rounded-full font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 transition-colors tracking-wide cursor-pointer"
                >
                  CONTINUE ON THIS SITE
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Card 2: Full Systems & Installations (DMI) */}
            <div className="flex flex-col bg-[#1a1f27]/80 rounded-xl overflow-hidden border border-gray-700/60 group transition-all duration-300 hover:border-gray-600">
              <div className="relative h-28 sm:h-32 lg:h-36 overflow-hidden">
                <img
                  src={residentialImg}
                  alt="Full Systems & Installations"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <img
                  src={logoLight}
                  alt="DMI"
                  className="absolute bottom-2 left-3 h-5 sm:h-6 object-contain drop-shadow-lg"
                />
              </div>
              <div className="p-3 sm:p-4 flex flex-col flex-grow">
                <h3 className="text-base sm:text-lg font-bold mb-1 text-white leading-snug">
                  Full Systems & Installations
                </h3>
                <p className="text-gray-400 text-xs sm:text-sm mb-3 flex-grow leading-relaxed">
                  Complete technology design, integration, and installation.
                </p>
                <a
                  href="https://digitalmediainnovations.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2 px-3 bg-transparent border border-white/40 hover:border-white hover:bg-white/5 text-white rounded-full font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 transition-all tracking-wide"
                >
                  CONTINUE TO DMI
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            {/* Card 3: Commercial AV Solutions (Colorado AV) */}
            <div className="flex flex-col bg-[#1a1f27]/80 rounded-xl overflow-hidden border border-gray-700/60 group transition-all duration-300 hover:border-gray-600">
              <div className="relative h-28 sm:h-32 lg:h-36 overflow-hidden">
                <img
                  src={commercialImg}
                  alt="Commercial AV Solutions"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <img
                  src={coloradoAvLogo}
                  alt="Colorado Audio Video"
                  className="absolute bottom-2 left-3 h-5 sm:h-6 object-contain drop-shadow-lg"
                />
              </div>
              <div className="p-3 sm:p-4 flex flex-col flex-grow">
                <h3 className="text-base sm:text-lg font-bold mb-1 text-white leading-snug">
                  Commercial AV Solutions
                </h3>
                <p className="text-gray-400 text-xs sm:text-sm mb-3 flex-grow leading-relaxed">
                  Professional audio, video, and conferencing solutions.
                </p>
                <a
                  href="https://www.coloradoaudiovideo.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2 px-3 bg-transparent border border-white/40 hover:border-white hover:bg-white/5 text-white rounded-full font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 transition-all tracking-wide"
                >
                  CONTINUE TO COAV
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

          </div>

          {/* Redirect notice */}
          <div className="flex items-center justify-center gap-1.5 mb-3 sm:mb-4 text-gray-500 text-[11px] sm:text-xs">
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
            <p>Clicking on DMI or COAV will redirect you to the selected business website.</p>
          </div>

          {/* Footer Divider + Tagline */}
          <div className="border-t border-gray-700/50 pt-3 sm:pt-4 flex flex-col items-center">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#f37021] mb-1.5" />
            <p className="text-gray-500 text-[10px] sm:text-xs tracking-[0.25em] font-semibold uppercase">
              Three Companies. One Family.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalPopup;
